import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { useState } from "react";
import type { VitalProgram, VitalPrograms } from "../useVitalsSettings";

type ProgramWithId = VitalProgram & { _id: string };

const toProgramWithId = (program: VitalProgram): ProgramWithId => ({
  ...program,
  _id: crypto.randomUUID(),
});

export const useEditProgramsView = (
  initialPrograms: VitalPrograms,
  vitalConfigurations: TellescopeModelFromName<"vital_configurations">[],
  onSave: (programs: VitalProgram[]) => Promise<void>,
  onCancel: () => void
) => {
  const [programs, setPrograms] = useState<ProgramWithId[]>(() => initialPrograms.programs.map(toProgramWithId));

  const addProgram = () => {
    setPrograms((prev) => [...prev, toProgramWithId({ title: "", configurationIds: [] })]);
  };

  const removeProgram = (id: string) => {
    setPrograms((prev) => prev.filter((p) => p._id !== id));
  };

  const updateProgramTitle = (id: string, title: string) => {
    setPrograms((prev) => prev.map((p) => (p._id === id ? { ...p, title } : p)));
  };

  const updateProgramConfigIds = (id: string, configurationIds: string[]) => {
    setPrograms((prev) => prev.map((p) => (p._id === id ? { ...p, configurationIds } : p)));
  };

  const reorderPrograms = (newPrograms: ProgramWithId[]) => {
    setPrograms(newPrograms);
  };

  const handleSave = async () => {
    await onSave(programs.map(({ _id: _, ...rest }) => rest));
  };

  const configOptions = vitalConfigurations.map((c) => ({ id: c.id, title: c.title }));

  return {
    programs,
    addProgram,
    removeProgram,
    updateProgramTitle,
    updateProgramConfigIds,
    reorderPrograms,
    handleSave,
    onCancel,
    configOptions,
  };
};
